import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getStatusMessage } from "@WhiteCap/Widgets/OsnQuotes/QuoteList";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    heading: string;
    text: string;
    extendedStyles?: SmallHeadingAndTextStyles;
    isQuoteListPage?: boolean;
}

export interface SmallHeadingAndTextStyles {
    wrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    heading?: TypographyProps;
    text?: TypographyProps;
    extendedStyles?: SmallHeadingAndTextStyles;
}

export const smallHeadingAndTextStyles: SmallHeadingAndTextStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            @media print {
                display: block;
            }
        `,
    },
    textWrapper: {
        css: css`
            width: 100px;
            border-radius: 13px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        `,
    },
    heading: {
        variant: "legend",
        css: css`
            color: #323232;
            font-size: 12px;
            text-transform: uppercase;
            font-weight: 400;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                        display: none;
                    `,
                    css`
                        font-size: 10px;
                        display: none;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    text: {
        css: css`
            width: 100%;
            font-size: 10px !important;
            color: #fff !important;
            font-style: normal;
            font-weight: 400 !important;
            line-height: 20px !important;
            text-align: center !important;
        `,
    },
};

const StatusLableAndHeading: React.FunctionComponent<OwnProps> = ({
    heading,
    text,
    isQuoteListPage,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(smallHeadingAndTextStyles, extendedStyles));

    const colorMapping: { [key: string]: string } = {
        "in progress": "#858585",
        "not scheduled": "#D3D3D3",
        delivered: "#3CB24D",
        shipped: "#ff8b36",
        "out for delivery": "#2EAAE8",
        "ready for pickup": "#2EAAE8",
        "picked up": "#3CB24D",
        "ready to pickup": "#2EAAE8",
        completed: "#3CB24D",
        draft: "#3CB24D",
    };

    const titleMapping: { [key: string]: string } = {
        "in progress": "In Progress",
        "not scheduled": "Not Scheduled",
        delivered: "Delivered",
        shipped: "Shipped",
        "out for delivery": "Out for Delivery",
        "ready for pickup": "Ready for Pickup",
        "picked up": "Picked up",
        "ready to pickup": "Ready to Pickup",
        completed: "Completed",
        draft: "Draft",
    };

    const getTitle = (text: string | undefined | null): string => {
        if (!text) {
            return "In Progress";
        }
        const key = text.trim().toLowerCase();
        return titleMapping[key] || "In Progress";
    };

    const getColor = (text: string): string => {
        return colorMapping[text?.toLowerCase()] || colorMapping["in progress"];
    };

    return (
        <StyledWrapper {...styles.wrapper} {...otherProps}>
            <Typography as="p" {...styles.heading} data-test-selector="heading">
                {heading}
            </Typography>
            <StyledWrapper style={{ backgroundColor: getColor(text?.toLocaleLowerCase()) }} {...styles.textWrapper}>
                {text && (
                    <Typography as="p" {...styles.text} data-test-selector={`text_${heading}`}>
                        {isQuoteListPage ? getStatusMessage(text) : getTitle(text)}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default StatusLableAndHeading;
